import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, User, LogOut, Star } from "lucide-react";
import { useProfileSection } from "../hooks/useProfile";
import subscriptionApi from "../api/subscriptionApi";

const ProfileDropdown = ({ onLogout, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [membershipTier, setMembershipTier] = useState("BASIC");
  const [refreshKey, setRefreshKey] = useState(0);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Get current user from localStorage
  useEffect(() => {
    const loadUserData = () => {
      // Check JWT token service first
      const userData = localStorage.getItem("trivana_user");
      if (userData) {
        const user = JSON.parse(userData);
        setCurrentUser(user);
        // Fetch fresh membership data
        fetchMembershipTier(user.user_id || user.id);
      } else {
        // Fallback to old 'user' key for backward compatibility
        const oldUserData = localStorage.getItem("user");
        if (oldUserData) {
          const user = JSON.parse(oldUserData);
          setCurrentUser(user);
          fetchMembershipTier(user.user_id || user.id);
        }
      }
    };

    // Fetch membership tier from API
    const fetchMembershipTier = async (userId) => {
      if (!userId) return;

      try {
        const response = await subscriptionApi.getCurrentPlan(userId);
        if (response.success && response.data) {
          const tier =
            response.data.membershipTier || response.data.planType || "BASIC";
          setMembershipTier(tier);
        }
      } catch (error) {
        console.error("Error fetching membership tier:", error);
      }
    };

    // Load user data initially
    loadUserData();

    // Listen for profile updates
    const handleProfileUpdate = (event) => {
      loadUserData();
    };

    // Listen for login events
    const handleLogin = (event) => {
      loadUserData();
    };

    // Listen for membership changes
    const handleMembershipChange = (event) => {
      loadUserData();
      setRefreshKey((prev) => prev + 1);
    };

    window.addEventListener("profileUpdated", handleProfileUpdate);
    window.addEventListener("login", handleLogin);
    window.addEventListener("membershipChanged", handleMembershipChange);

    return () => {
      window.removeEventListener("profileUpdated", handleProfileUpdate);
      window.removeEventListener("login", handleLogin);
      window.removeEventListener("membershipChanged", handleMembershipChange);
    };
  }, [refreshKey]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch basic profile and subscription data using correct user ID
  const userId = currentUser?.user_id || currentUser?.id;
  const { data: basicProfile, loading: profileLoading } = useProfileSection(
    userId,
    "basic"
  );
  const { data: subscriptionData, loading: subscriptionLoading } =
    useProfileSection(userId, "subscription");

  // Prepare user data from API or fallback to current user
  const userData = {
    name: basicProfile?.fullName || currentUser?.name || "User",
    email: basicProfile?.email || currentUser?.email || "user@example.com",
    avatar:
      basicProfile?.profilePicture ||
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face",
    membership:
      membershipTier ||
      subscriptionData?.membershipTier ||
      subscriptionData?.planType ||
      "BASIC",
    rewardPoints:
      subscriptionData?.rewardPoints || subscriptionData?.loyaltyPoints || 0,
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleProfileClick = () => {
    setIsOpen(false);
    // Call onNavigate to close mobile menu if provided
    if (onNavigate) {
      onNavigate();
    }
    navigate("/profile");
  };

  return (
    <>
      <div style={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            padding: isMobile ? "8px" : "10px 16px",
            borderRadius: "10px",
            background: "white",
            border: "1.5px solid rgba(238, 46, 36, 0.2)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            cursor: "pointer",
            transition: "all 0.2s ease",
            fontFamily: "'Inter', sans-serif",
            width: "100%",
            maxWidth: isMobile ? "110px" : "250px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#EE2E24";
            e.currentTarget.style.background = "rgba(238, 46, 36, 0.05)";
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(238, 46, 36, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "rgba(238, 46, 36, 0.2)";
            e.currentTarget.style.background = "white";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.05)";
          }}
        >
          <img
            src={userData.avatar}
            alt={userData.name}
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              objectFit: "cover",
              border: "1.5px solid #EE2E24",
            }}
          />
          {!isMobile && (
            <span
              style={{
                fontSize: "0.85rem",
                fontWeight: "700",
                color: "#1a1a1a",
                maxWidth: "120px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginLeft: "4px",
              }}
            >
              {userData.name.split(" ")[0]}
            </span>
          )}
          <ChevronDown
            style={{
              width: "14px",
              height: "14px",
              color: "#EE2E24",
              transition: "transform 0.2s ease",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              flexShrink: 0,
              marginLeft: "auto",
            }}
          />
        </button>

        {isOpen && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              // Position on the right for mobile to prevent overflow
              right: 0,
              width: isMobile ? "240px" : "300px",
              background: "white",
              borderRadius: "14px",
              boxShadow: "0 15px 45px rgba(0, 0, 0, 0.18)",
              border: "1px solid rgba(0, 0, 0, 0.08)",
              zIndex: 10001,
              overflow: "hidden",
              animation: "slideDown 0.25s cubic-bezier(0, 0, 0.2, 1)",
            }}
          >
            {/* Profile Header */}
            <div
              style={{
                padding: "14px",
                background: "linear-gradient(135deg, rgba(238, 46, 36, 0.03) 0%, rgba(255, 196, 0, 0.03) 100%)",
                borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <img
                src={userData.avatar}
                alt={userData.name}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2px solid #EE2E24",
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p
                  style={{
                    fontWeight: "800",
                    color: "#1a1a1a",
                    fontSize: "0.9rem",
                    margin: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {userData.name}
                </p>
                <div
                  style={{
                    fontSize: "0.65rem",
                    color: "#EE2E24",
                    fontWeight: "800",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    marginTop: "2px",
                  }}
                >
                  <Star size={10} fill="#FFC400" stroke="#FFC400" />
                  {userData.membership} MEMBER
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div style={{ padding: "6px" }}>
              <button
                onClick={handleProfileClick}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  padding: "10px 12px",
                  fontSize: "0.82rem",
                  fontWeight: "700",
                  color: "#4a5568",
                  background: "transparent",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#F9FAFB";
                  e.currentTarget.style.color = "#EE2E24";
                  e.currentTarget.style.paddingLeft = "16px";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#4a5568";
                  e.currentTarget.style.paddingLeft = "12px";
                }}
              >
                <User size={14} style={{ marginRight: "10px" }} />
                View Profile
              </button>

              <div style={{ height: "1px", background: "#f0f0f0", margin: "4px 10px" }} />

              <button
                onClick={() => {
                  onLogout();
                  setIsOpen(false);
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  padding: "10px 12px",
                  fontSize: "0.82rem",
                  fontWeight: "700",
                  color: "#EE2E24",
                  background: "transparent",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(238, 46, 36, 0.05)";
                  e.currentTarget.style.paddingLeft = "16px";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.paddingLeft = "12px";
                }}
              >
                <LogOut size={14} style={{ marginRight: "10px" }} />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default ProfileDropdown;
